import React from "react";
import baseStyles from "../../style.module.scss";
import ReviewHeader from "../common/ReviewHeader";
import { useFormikContext } from "formik";

const SectionHeader = ({ title }) => (
  <div className={baseStyles.sectionHeader}>
    <span className={baseStyles.sectionTitle}>{title}</span>
    <div className={baseStyles.sectionLine} />
  </div>
);

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const SEASONALITY_OPTIONS = [
  { key: "pct_annual_sales", label: "% of total annual sales processed in this month" },
  { key: "max_monthly_card_sales", label: "Estimated maximum monthly card sales (USD)" },
  { key: "min_monthly_card_sales", label: "Estimated minimum monthly card sales (USD)" },
  { key: "monthly_total_sales", label: "Estimated monthly total sales (card and non-card) for the company in this month (USD)" },
];

const BUSINESS_ACTIVITY_OPTIONS = [
  { label: "Travel Agent", value: "travel_agent" },
  { label: "Tour Operator", value: "tour_operator" },
  { label: "Event Ticket Sellers (Non-Sporting)", value: "event_ticket_sellers_non_sporting" },
  { label: "Stand Alone Travel Ticket Sellers & Tour Operators", value: "stand_alone_travel_ticket_sellers_tour_operators" },
  { label: "Event Organisers (Non-Sporting)", value: "event_organisers_non_sporting" },
  { label: "Event Organisers (Sporting)", value: "event_organisers_sporting" },
  { label: "Third Party Event/Ticket Sellers/Touting", value: "third_party_event_ticket_sellers_touting" },
  { label: "Secondary Ticket Market (“C2C) / “Ticket Exchanges", value: "secondary_ticket_market_c2c_ticket_exchanges" },
  { label: "Other", value: "other" },
];

const DELIVERY_PERIODS = [
  { key: "1_7_days", label: "1-7 days" },
  { key: "8_14_days", label: "8-14 days" },
  { key: "15_30_days", label: "15-30 days" },
  { key: "31_60_days", label: "31-60 days" },
  { key: "61_90_days", label: "61-90 days" },
  { key: "91_120_days", label: "91-120 days" },
  { key: "121_150_days", label: "121-150 days" },
  { key: "151_180_days", label: "151-180 days" },
  { key: "over_180_days", label: "Over 180 days" },
];

function TravelAddendumReview({ isOpen, onToggle, onEdit }) {
  const { values } = useFormikContext();

  return (
    <div className={baseStyles.reviewSectionContainer}>
      <ReviewHeader title="Travel Addendum" isOpen={isOpen} onToggle={onToggle} onEdit={onEdit} />

      {isOpen && (
        <div className={baseStyles.reviewContent}>
          {/* Business Activities */}
          <div className={baseStyles.section}>
            <SectionHeader title="Business Activities" />
            <div className={baseStyles.detailsGrid}>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Business Activities</div>
                <div className={baseStyles.detailValue}>
                  {values.travel_business_activities?.length > 0
                    ? values.travel_business_activities
                        .map((val) => BUSINESS_ACTIVITY_OPTIONS.find((opt) => opt.value === val)?.label ?? val)
                        .join(", ")
                    : "-"}
                </div>
              </div>
              {values.travel_business_activities?.includes("other") && (
                <div className={baseStyles.detailItem}>
                  <div className={baseStyles.detailLabel}>Other - please provide further details</div>
                  <div className={baseStyles.detailValue}>
                    {values.travel_other_business_activity || "-"}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Gross Annual Turnover */}
          <div className={baseStyles.section}>
            <SectionHeader title="Gross Annual Turnover" />
            <div className={baseStyles.detailsGrid}>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Sales of third party packages</div>
                <div className={baseStyles.detailValue}>{values.travel_sales_third_party_packages ? `${values.travel_sales_third_party_packages}%` : "-"}</div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Agency sale of accommodation only</div>
                <div className={baseStyles.detailValue}>{values.travel_agency_accommodation_only ? `${values.travel_agency_accommodation_only}%` : "-"}</div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Agency sale of non-flight transport only</div>
                <div className={baseStyles.detailValue}>{values.travel_agency_non_flight_transport ? `${values.travel_agency_non_flight_transport}%` : "-"}</div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Flight only - own merchant facility</div>
                <div className={baseStyles.detailValue}>{values.travel_flight_own_merchant ? `${values.travel_flight_own_merchant}%` : "-"}</div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Flight only - airline merchant facility</div>
                <div className={baseStyles.detailValue}>{values.travel_flight_airline_merchant ? `${values.travel_flight_airline_merchant}%` : "-"}</div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Other sales as agent</div>
                <div className={baseStyles.detailValue}>{values.travel_other_sales_agent ? `${values.travel_other_sales_agent}%` : "-"}</div>
              </div>

              {values.travel_other_sales_agent && values.travel_other_sales_agent !== "0" && (
                <div className={baseStyles.detailItem}>
                  <div className={baseStyles.detailLabel}>Describe 'Other sales' as appropriate</div>
                  <div className={baseStyles.detailValue}>{values.travel_other_sales_description ? `${values.travel_other_sales_description}` : "-"}</div>
                </div>
              )}

              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Total Gross Turnover</div>
                <div className={baseStyles.detailValue}>{values.travel_total_gross_turnover ? `${values.travel_total_gross_turnover}` : "-"}</div>
              </div>
            </div>
          </div>

          {/* Airlines / Travel Suppliers */}
          {values.travel_airline_suppliers?.length > 0 && (
            <div className={baseStyles.section}>
              <SectionHeader title="Airlines / Travel Suppliers" />
              <div className={baseStyles.supplierGrid5Col}>
                <div className={baseStyles.supplierGridHeader}>Name</div>
                <div className={baseStyles.supplierGridHeader}>Country of base</div>
                <div className={baseStyles.supplierGridHeader}>Payment terms</div>
                <div className={baseStyles.supplierGridHeader}>% share of all flight spend</div>
                <div className={baseStyles.supplierGridHeader}>Average % of package cost</div>
                {values.travel_airline_suppliers.map((supplier, index) => (
                  <React.Fragment key={index}>
                    <div className={baseStyles.supplierGridCell}>{supplier.name || "-"}</div>
                    <div className={baseStyles.supplierGridCell}>{supplier.country || "-"}</div>
                    <div className={baseStyles.supplierGridCell}>{supplier.payment_terms || "-"}</div>
                    <div className={baseStyles.supplierGridCell}>{supplier.flight_spend_share != null && supplier.flight_spend_share !== "" ? `${supplier.flight_spend_share}%` : "-"}</div>
                    <div className={baseStyles.supplierGridCell}>{supplier.average_package_cost != null && supplier.average_package_cost !== "" ? `${supplier.average_package_cost}%` : "-"}</div>
                  </React.Fragment>
                ))}
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Total % spend on Travel</div>
                <div className={baseStyles.detailValue}>{values.travel_total_spend_travel != null && values.travel_total_spend_travel !== "" ? `${values.travel_total_spend_travel}%` : "-"}</div>
              </div>
            </div>
          )}

          {/* Hotels/Accommodation Suppliers */}
          {values.travel_hotel_suppliers?.length > 0 && (
            <div className={baseStyles.section}>
              <SectionHeader title="Hotels / Accommodation Suppliers" />
              <div className={baseStyles.supplierGrid5Col}>
                <div className={baseStyles.supplierGridHeader}>Name</div>
                <div className={baseStyles.supplierGridHeader}>Country of base</div>
                <div className={baseStyles.supplierGridHeader}>Payment terms</div>
                <div className={baseStyles.supplierGridHeader}>% share of all hotel spend</div>
                <div className={baseStyles.supplierGridHeader}>Average % of package cost</div>
                {values.travel_hotel_suppliers.map((supplier, index) => (
                  <React.Fragment key={index}>
                    <div className={baseStyles.supplierGridCell}>{supplier.name || "-"}</div>
                    <div className={baseStyles.supplierGridCell}>{supplier.country || "-"}</div>
                    <div className={baseStyles.supplierGridCell}>{supplier.payment_terms || "-"}</div>
                    <div className={baseStyles.supplierGridCell}>{supplier.hotel_spend_share != null && supplier.hotel_spend_share !== "" ? `${supplier.hotel_spend_share}%` : "-"}</div>
                    <div className={baseStyles.supplierGridCell}>{supplier.average_package_cost != null && supplier.average_package_cost !== "" ? `${supplier.average_package_cost}%` : "-"}</div>
                  </React.Fragment>
                ))}
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Total % spend on Accommodation</div>
                <div className={baseStyles.detailValue}>{values.travel_total_spend_accommodation != null && values.travel_total_spend_accommodation !== "" ? `${values.travel_total_spend_accommodation}%` : "-"}</div>
              </div>
            </div>
          )}

          {/* Local Services */}
          {values.travel_local_services?.length > 0 && (
            <div className={baseStyles.section}>
              <SectionHeader title="Local Services" />
              <div className={baseStyles.supplierGrid5Col}>
                <div className={baseStyles.supplierGridHeader}>Service Provider Name</div>
                <div className={baseStyles.supplierGridHeader}>Country of base</div>
                <div className={baseStyles.supplierGridHeader}>Payment terms</div>
                <div className={baseStyles.supplierGridHeader}>% share of 'other' service provider spend</div>
                <div className={baseStyles.supplierGridHeader}>Avg % of package cost</div>
                {values.travel_local_services.map((service, index) => (
                  <React.Fragment key={index}>
                    <div className={baseStyles.supplierGridCell}>{service.name || "-"}</div>
                    <div className={baseStyles.supplierGridCell}>{service.country || "-"}</div>
                    <div className={baseStyles.supplierGridCell}>{service.payment_terms || "-"}</div>
                    <div className={baseStyles.supplierGridCell}>{service.service_spend_share != null && service.service_spend_share !== "" ? `${service.service_spend_share}%` : "-"}</div>
                    <div className={baseStyles.supplierGridCell}>{service.average_package_cost != null && service.average_package_cost !== "" ? `${service.average_package_cost}%` : "-"}</div>
                  </React.Fragment>
                ))}
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Total % spend on Other elements</div>
                <div className={baseStyles.detailValue}>{values.travel_total_spend_other != null && values.travel_total_spend_other !== "" ? `${values.travel_total_spend_other}%` : "-"}</div>
              </div>
            </div>
          )}

          {/* Countries of Travel */}
          {values.travel_countries?.length > 0 && (
            <div className={baseStyles.section}>
              <SectionHeader title="Countries of Travel" />
              <div className={baseStyles.countriesGrid2Col}>
                <div className={baseStyles.countriesGridHeader}>Country Name</div>
                <div className={baseStyles.countriesGridHeader}>% of overall trade</div>
                {values.travel_countries.map((country, index) => (
                  <React.Fragment key={index}>
                    <div className={baseStyles.countriesGridCell}>{country.name || "-"}</div>
                    <div className={baseStyles.countriesGridCell}>{country.trade_share != null && country.trade_share !== "" ? `${country.trade_share}%` : "-"}</div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Seasonality */}
          <div className={baseStyles.section}>
            <SectionHeader title="Seasonality" />
            <div className={baseStyles.seasonalityReviewGrid}>
              <div className={baseStyles.seasonalityReviewHeader} />
              {SEASONALITY_OPTIONS.map((option) => (
                <div key={option.key} className={baseStyles.seasonalityReviewColumnHeader}>
                  {option.label}
                </div>
              ))}
              {MONTHS.map((month) => (
                <React.Fragment key={month}>
                  <div className={baseStyles.seasonalityReviewRowHeader}>{month}</div>
                  {SEASONALITY_OPTIONS.map((option) => {
                    const fieldName = `travel_seasonality_${option.key}_${month.toLowerCase()}`;
                    return (
                      <div key={fieldName} className={baseStyles.seasonalityReviewCell}>
                        {values[fieldName] ? `${values[fieldName]}` : "-"}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Customer Payment Terms */}
          <div className={baseStyles.section}>
            <SectionHeader title="Customer Payment Terms" />
            <div className={baseStyles.detailsGrid}>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Average deposit paid (%)</div>
                <div className={baseStyles.detailValue}>{values.travel_average_deposit_percentage != null && values.travel_average_deposit_percentage !== "" ? `${values.travel_average_deposit_percentage}%` : "-"}</div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Average time between deposit payment and travel (months)</div>
                <div className={baseStyles.detailValue}>{values.travel_average_time_deposit_travel != null && values.travel_average_time_deposit_travel !== "" ? values.travel_average_time_deposit_travel : "-"}</div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Average time between balance payment and travel (months)</div>
                <div className={baseStyles.detailValue}>{values.travel_average_time_balance_travel != null && values.travel_average_time_balance_travel !== "" ? values.travel_average_time_balance_travel : "-"}</div>
              </div>
            </div>
            <p className={baseStyles.deliverySubtitle}>PERIOD FROM DATE OF CARD TRANSACTION TO DATE OF TRAVEL/EVENT/STAY</p>
            <div className={baseStyles.detailsGrid}>
              {DELIVERY_PERIODS.map((period) => {
                const fieldName = `travel_delivery_${period.key}`;
                const value = values[fieldName];
                return (
                  <div className={baseStyles.detailItem} key={period.key}>
                    <div className={baseStyles.detailLabel}>{period.label} (%)</div>
                    <div className={baseStyles.detailValue}>{value != null && value !== "" ? `${value}%` : "-"}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Additional Information */}
          <div className={baseStyles.section}>
            <SectionHeader title="Additional Information" />
            <div className={baseStyles.detailsGrid}>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Consumer protection</div>
                <div className={baseStyles.detailValue}>{values.travel_consumer_protection ? `${values.travel_consumer_protection}` : "-"}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TravelAddendumReview;
